/**
 * The administrator desk.
 *
 * Everything an owner needs to run paid access: create an account, approve a
 * request, renew or extend a plan, suspend, reset a password, delete. The list
 * is the source of truth after every mutation — it is refetched rather than
 * patched locally, so the counts next to the table can never disagree with it.
 */

import { Fragment, FormEvent, useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  Ban,
  Check,
  Clock,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import {
  AdminUser,
  ApiError,
  AuditEntry,
  Stats,
  api,
  daysLeft,
  formatBytes,
  formatDate,
} from './api';
import { Modal } from '../ui/widgets/common';

type Tab = 'accounts' | 'audit';

const EMPTY_CREATE = () => ({
  username: '',
  password: '',
  plan: 'weekly',
  expiresAt: addDays(null, 7),
  role: 'user',
  note: '',
});

function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function addDays(iso: string | null, days: number): string {
  const base = iso ? Date.parse(iso) : Date.now();
  const start = Number.isFinite(base) ? Math.max(base, Date.now()) : Date.now();
  const next = new Date(start + days * 86400000);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
}

export function AdminDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const [tab, setTab] = useState<Tab>('accounts');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_CREATE);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteArmed, setDeleteArmed] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const [userResult, statsResult, auditResult] = await Promise.all([
        api<{ users: AdminUser[] }>('/api/admin/users'),
        api<{ stats: Stats }>('/api/admin/stats'),
        api<{ entries: AuditEntry[] }>('/api/admin/audit?limit=80'),
      ]);
      setUsers(userResult.users);
      setStats(statsResult.stats);
      setAudit(auditResult.entries);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load the account list.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const flash = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(null), 4000);
  };

  const createAccount = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api('/api/admin/users', {
        method: 'POST',
        body: {
          username: form.username.trim(),
          password: form.password,
          role: form.role,
          status: 'active',
          plan: form.plan.trim() || 'custom',
          expiresAt: form.expiresAt || null,
          note: form.note.trim(),
        },
      });
      flash(`Account ${form.username.trim()} created.`);
      setForm(EMPTY_CREATE());
      setCreating(false);
      await load(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create the account.');
    } finally {
      setSaving(false);
    }
  };

  const patchUser = async (user: AdminUser, changes: Record<string, unknown>, message: string) => {
    setBusyId(user.id);
    setError(null);
    try {
      await api(`/api/admin/users/${user.id}`, { method: 'PATCH', body: changes });
      flash(message);
      await load(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not update the account.');
    } finally {
      setBusyId(null);
    }
  };

  const removeUser = async (user: AdminUser) => {
    if (deleteArmed !== user.id) {
      setDeleteArmed(user.id);
      window.setTimeout(() => setDeleteArmed((current) => (current === user.id ? null : current)), 4000);
      return;
    }
    setDeleteArmed(null);
    setBusyId(user.id);
    setError(null);
    try {
      await api(`/api/admin/users/${user.id}`, { method: 'DELETE' });
      flash(`Account ${user.username} deleted.`);
      await load(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not delete the account.');
    } finally {
      setBusyId(null);
    }
  };

  const pending = users.filter((user) => user.status === 'pending').length;

  return (
    <Modal title="Administration" onClose={onClose} width={1040}>
      <div className="admin-toolbar">
        <div className="admin-stats">
          <Stat label="Accounts" value={stats?.users ?? users.length} />
          <Stat label="Active" value={stats?.active ?? 0} tone="good" />
          <Stat label="Pending" value={stats?.pending ?? pending} tone={pending > 0 ? 'warn' : undefined} />
          <Stat label="Projects" value={stats?.projects ?? 0} />
          <Stat label="Storage" value={formatBytes(stats?.projectBytes ?? 0)} />
          <span className="admin-driver">{stats?.storageDriver ?? '—'}</span>
        </div>
        <div className="row">
          <button className="btn icon" onClick={() => void load()} title="Refresh">
            <RefreshCw size={14} />
          </button>
          <button className="btn primary" onClick={() => setCreating((value) => !value)}>
            {creating ? <X size={14} /> : <Plus size={14} />}
            {creating ? 'Cancel' : 'New account'}
          </button>
        </div>
      </div>

      <div className="admin-tabs">
        <button className={tab === 'accounts' ? 'active' : ''} onClick={() => setTab('accounts')}>
          Accounts <span className="count">{users.length}</span>
        </button>
        <button className={tab === 'audit' ? 'active' : ''} onClick={() => setTab('audit')}>
          Activity <span className="count">{audit.length}</span>
        </button>
        <span className="spacer" />
        {notice ? <span className="admin-notice">{notice}</span> : null}
      </div>

      {error ? (
        <p className="form-note error">
          <AlertCircle size={14} /> {error}
        </p>
      ) : null}

      {creating ? (
        <form className="admin-create" onSubmit={createAccount}>
          <div className="admin-create-head">
            <UserPlus size={15} />
            <strong>New account</strong>
            <span className="hint-text">Created active unless you choose otherwise.</span>
          </div>
          <div className="admin-create-grid">
            <label className="field">
              <span>Username</span>
              <input
                className="input"
                value={form.username}
                onChange={(event) => setForm({ ...form, username: event.target.value })}
                placeholder="handle"
                required
                minLength={3}
                maxLength={32}
              />
            </label>
            <label className="field">
              <span>Password</span>
              <input
                className="input"
                type="text"
                value={form.password}
                onChange={(event) => setForm({ ...form, password: event.target.value })}
                placeholder="at least 8 characters"
                required
                minLength={8}
              />
            </label>
            <label className="field">
              <span>Plan</span>
              <input
                className="input"
                value={form.plan}
                onChange={(event) => setForm({ ...form, plan: event.target.value })}
                placeholder="weekly"
                maxLength={40}
              />
            </label>
            <label className="field">
              <span>Expires</span>
              <input
                className="input"
                type="date"
                value={form.expiresAt}
                onChange={(event) => setForm({ ...form, expiresAt: event.target.value })}
              />
            </label>
            <label className="field">
              <span>Role</span>
              <select
                className="select"
                value={form.role}
                onChange={(event) => setForm({ ...form, role: event.target.value })}
              >
                <option value="user">Member</option>
                <option value="admin">Administrator</option>
              </select>
            </label>
            <label className="field">
              <span>Note</span>
              <input
                className="input"
                value={form.note}
                onChange={(event) => setForm({ ...form, note: event.target.value })}
                placeholder="Discord handle, payment ref, notes"
                maxLength={1000}
              />
            </label>
          </div>
          <div className="row">
            <span className="hint-text">New accounts default to one week. Empty expiry never lapses.</span>
            <span className="spacer" />
            <button className="btn primary" type="submit" disabled={saving}>
              {saving ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Create account
            </button>
          </div>
        </form>
      ) : null}

      {tab === 'accounts' ? (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Status</th>
                <th>Plan</th>
                <th>Expires</th>
                <th>Projects</th>
                <th>Last sign-in</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="admin-empty">
                    <Loader2 size={16} className="spin" /> Loading accounts…
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="admin-empty">
                    No accounts yet.
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const remaining = daysLeft(user.expiresAt);
                  const expired = user.expired;
                  const isBusy = busyId === user.id;
                  return (
                    <Fragment key={user.id}>
                      <tr className={editingId === user.id ? 'editing' : ''}>
                        <td>
                          <div className="admin-user">
                            <span className="admin-avatar">{user.username.slice(0, 1).toUpperCase()}</span>
                            <div>
                              <strong>
                                {user.username}
                                {user.role === 'admin' ? (
                                  <span className="admin-role" title="Administrator">
                                    <ShieldCheck size={12} />
                                  </span>
                                ) : null}
                              </strong>
                              <span>{user.note || '—'}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              expired ? 'bad' : user.status === 'active' ? 'good' : user.status === 'pending' ? 'warn' : 'bad'
                            }`}
                          >
                            {expired ? 'expired' : user.status}
                          </span>
                        </td>
                        <td>{user.plan}</td>
                        <td>
                          {user.expiresAt ? (
                            <span className={expired ? 'expired' : ''}>
                              {formatDate(user.expiresAt)}
                              {remaining !== null && !expired ? <em> · {remaining}d</em> : null}
                            </span>
                          ) : (
                            <span className="dim">never</span>
                          )}
                        </td>
                        <td>
                          {user.projects}
                          <span className="dim"> · {formatBytes(user.projectBytes)}</span>
                        </td>
                        <td className="dim">{formatDate(user.lastLoginAt)}</td>
                        <td>
                          <div className="admin-actions">
                            {isBusy ? <Loader2 size={14} className="spin" /> : null}
                            {user.status === 'pending' ? (
                              <button
                                className="btn small good"
                                onClick={() =>
                                  void patchUser(
                                    user,
                                    {
                                      status: 'active',
                                      plan: user.plan === 'trial' ? 'weekly' : user.plan,
                                      expiresAt: addDays(null, 7),
                                    },
                                    `${user.username} approved for 7 days.`,
                                  )
                                }
                                disabled={isBusy}
                                title="Approve for 7 days"
                              >
                                <Check size={13} /> Approve
                              </button>
                            ) : null}
                            <button
                              className="btn small"
                              onClick={() => setEditingId(editingId === user.id ? null : user.id)}
                              title="Edit account"
                            >
                              <Pencil size={13} /> Edit
                            </button>
                            {user.expiresAt ? (
                              <button
                                className="btn small"
                                onClick={() =>
                                  void patchUser(
                                    user,
                                    { expiresAt: addDays(user.expiresAt, 7) },
                                    `${user.username} renewed for 7 days.`,
                                  )
                                }
                                disabled={isBusy}
                                title="Extend by 7 days"
                              >
                                <Clock size={13} /> +7d
                              </button>
                            ) : null}
                            {user.status === 'active' ? (
                              <button
                                className="btn small danger"
                                onClick={() => void patchUser(user, { status: 'suspended' }, `${user.username} suspended.`)}
                                disabled={isBusy}
                                title="Suspend"
                              >
                                <Ban size={13} />
                              </button>
                            ) : user.status === 'suspended' ? (
                              <button
                                className="btn small"
                                onClick={() => void patchUser(user, { status: 'active' }, `${user.username} restored.`)}
                                disabled={isBusy}
                                title="Restore"
                              >
                                <Check size={13} />
                              </button>
                            ) : null}
                            <button
                              className={`btn small ${deleteArmed === user.id ? 'danger solid' : 'danger'}`}
                              onClick={() => void removeUser(user)}
                              disabled={isBusy}
                              title={deleteArmed === user.id ? 'Click again to delete' : 'Delete account'}
                            >
                              <Trash2 size={13} />
                              {deleteArmed === user.id ? 'Confirm' : ''}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {editingId === user.id ? (
                        <tr key={`edit-${user.id}`} className="admin-edit-row">
                          <td colSpan={7}>
                            <EditUser
                              user={user}
                              onCancel={() => setEditingId(null)}
                              onSaved={async (message) => {
                                setEditingId(null);
                                flash(message);
                                await load(true);
                              }}
                              onError={(message) => setError(message)}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-audit">
          {audit.length === 0 ? (
            <p className="admin-empty">No activity recorded yet.</p>
          ) : (
            audit.map((entry) => (
              <div className="audit-row" key={entry.id}>
                <time>{new Date(entry.at).toLocaleString()}</time>
                <span className="audit-action">{entry.action}</span>
                <span className="audit-actor">{entry.actor}</span>
                <span className="audit-detail">{entry.detail}</span>
              </div>
            ))
          )}
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value, tone }: { label: string; value: number | string; tone?: 'good' | 'warn' }): JSX.Element {
  return (
    <span className={`admin-stat${tone ? ` ${tone}` : ''}`}>
      <b>{value}</b>
      <span>{label}</span>
    </span>
  );
}

function EditUser({
  user,
  onCancel,
  onSaved,
  onError,
}: {
  user: AdminUser;
  onCancel: () => void;
  onSaved: (message: string) => void | Promise<void>;
  onError: (message: string) => void;
}): JSX.Element {
  const [plan, setPlan] = useState(user.plan);
  const [status, setStatus] = useState(user.status);
  const [role, setRole] = useState(user.role);
  const [expiresAt, setExpiresAt] = useState(toDateInput(user.expiresAt));
  const [note, setNote] = useState(user.note);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api(`/api/admin/users/${user.id}`, {
        method: 'PATCH',
        body: {
          plan: plan.trim() || 'custom',
          status,
          role,
          expiresAt: expiresAt || null,
          note,
          ...(password ? { password } : {}),
        },
      });
      await onSaved(`${user.username} updated.`);
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'Could not save the account.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="admin-edit" onSubmit={save}>
      <div className="admin-edit-grid">
        <label className="field">
          <span>Plan</span>
          <input className="input" value={plan} onChange={(event) => setPlan(event.target.value)} maxLength={40} />
        </label>
        <label className="field">
          <span>Status</span>
          <select className="select" value={status} onChange={(event) => setStatus(event.target.value as AdminUser['status'])}>
            <option value="active">active</option>
            <option value="pending">pending</option>
            <option value="suspended">suspended</option>
          </select>
        </label>
        <label className="field">
          <span>Role</span>
          <select className="select" value={role} onChange={(event) => setRole(event.target.value as AdminUser['role'])}>
            <option value="user">member</option>
            <option value="admin">administrator</option>
          </select>
        </label>
        <label className="field">
          <span>Expires</span>
          <input
            className="input"
            type="date"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </label>
        <label className="field">
          <span>Note</span>
          <input className="input" value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} />
        </label>
        <label className="field">
          <span>New password</span>
          <input
            className="input"
            type="text"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="leave blank to keep"
            minLength={8}
          />
        </label>
      </div>
      <div className="row">
        <span className="hint-text">Empty expiry means the account never lapses.</span>
        <span className="spacer" />
        <button className="btn" type="button" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Save changes
        </button>
      </div>
    </form>
  );
}
